import { Dialog } from '../../../../../../widgets/dialog';
import React from 'react';
import Select from 'react-select';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';

interface ComposerRetargetAttributeOutput {
    // Mapping from old object/material id to new object/material id
    objectMap?: Record<string, string>;
    materialMap?: Record<string, string>;
}
interface ComposerRetargetAttributeDialogProps {
    objectsList: BABYLON.AbstractMesh[];
    materialsList: BABYLON.Material[];
    usedMaterialsList: BABYLON.Material[];
    usedObjectsList: BABYLON.AbstractMesh[];
    onConfirm: (result: ComposerRetargetAttributeOutput) => void;
}
const ComposerSaveAttributeDialogWindow = styled(Dialog)`
    max-width: 600px;
    min-height: 600px;
`;
const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
    min-height: 300px;
`;
const FormInline = styled.div`
display: flex
;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-around;
    align-items: center;
	min-width: 300px;
    gap: 15px;
}
`;
const ComposerRetargetAttributeDialog: React.FC<ComposerRetargetAttributeDialogProps> = ({
    materialsList: sceneMaterialList,
    objectsList: sceneMeshList,
    usedMaterialsList: materialsList,
    usedObjectsList: objectsList,
    onConfirm
}) => {
    const { filteredMaterialList, filteredObjectList } = React.useMemo(() => {
        const matMap: { [id: string]: BABYLON.Material } = {};
        materialsList.forEach((mat) => {
            matMap[mat.id] = mat;
        });
        const objMap: { [id: string]: BABYLON.AbstractMesh } = {};
        objectsList.forEach((obj) => {
            objMap[obj.id] = obj;
        });
        return { filteredMaterialList: Object.values(matMap), filteredObjectList: Object.values(objMap) };
    }, [materialsList, objectsList]);
    const [materialMap, setMaterialMap] = React.useState<{ [oldId: string]: string }>({});
    const [objectMap, setObjectMap] = React.useState<{ [oldId: string]: string }>({});

    const handleConfirm = () => {
        const result: ComposerRetargetAttributeOutput = {
            materialMap: materialMap,
            objectMap: objectMap
        };
        onConfirm(result);
    };

    return (
        <Dialog
            title={T._('Match objects and materials', 'Admin')}
            windowDecorator={ComposerSaveAttributeDialogWindow}
            buttons={[{ label: T._('Confirm', 'Admin'), onClick: handleConfirm }]}
        >
            <Container>
                {filteredMaterialList.length === 0 && filteredObjectList.length === 0 && (
                    <div>{T._('No materials or objects available for the selected element.', 'Admin')}</div>
                )}
                {(filteredMaterialList.length > 0 || filteredObjectList.length > 0) && (
                    <p>{T._('Select the new scene objects and materials that will replace those used by the actions.', 'Admin')}</p>
                )}
                {filteredObjectList.length > 0 && (
                    <>
                        <h3>{T._('Objects', 'Admin')}</h3>
                        {filteredObjectList.map((obj) => (
                            <FormInline key={obj.id}>
                                <span>{obj.name}</span>
                                <Select
                                    options={sceneMeshList.filter((o) => o.id !== obj.id).map((o) => ({ label: o.name, value: o.id }))}
                                    placeholder={T._('Select Object', 'Admin')}
                                    isClearable
                                    onChange={(sel) => {
                                        if (!sel) {
                                            const copy = { ...objectMap };
                                            delete copy[obj.id];
                                            setObjectMap(copy);
                                            return;
                                        }
                                        const selectedValue = sel?.value || '';
                                        setObjectMap((old) => ({ ...old, [obj.id]: selectedValue }));
                                    }}
                                />
                            </FormInline>
                        ))}
                    </>
                )}
                {filteredMaterialList.length > 0 && (
                    <>
                        <h3>{T._('Materials', 'Admin')}</h3>
                        {filteredMaterialList.map((mat) => (
                            <FormInline key={mat.id}>
                                <span>{mat.name}</span>
                                <Select
                                    options={sceneMaterialList.filter((m) => m.id !== mat.id).map((m) => ({ label: m.name, value: m.id }))}
                                    placeholder={T._('Select Material', 'Admin')}
                                    isClearable
                                    onChange={(sel) => {
                                        if (!sel) {
                                            const copy = { ...materialMap };
                                            delete copy[mat.id];
                                            setMaterialMap(copy);
                                            return;
                                        }
                                        const selectedValue = sel?.value || '';
                                        setMaterialMap((old) => ({ ...old, [mat.id]: selectedValue }));
                                    }}
                                />
                            </FormInline>
                        ))}
                    </>
                )}
            </Container>
        </Dialog>
    );
};

export default ComposerRetargetAttributeDialog;
